import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { isEmpty, isNil } from 'lodash'
import { IconCheck, IconAlertTriangle } from '@tabler/icons-react'

import { fetchingAPI } from '@/api/fetchingAPI'
import { apiService } from '@/api/apiService'
import { Progress } from '@/components/progress/Progress'

const POLL_INTERVAL_MS = 10000
// A backfill runs for about an hour, so a transient 500 shouldn't kill the bar —
// but an endpoint that keeps failing should stop being asked.
const MAX_CONSECUTIVE_FAILURES = 3
// A backfill that never reaches a terminal status — a wedged job, or a status
// string this build doesn't know — would otherwise be polled for as long as the
// tab stays open. Two hours is well past the ~1 hour a real one takes.
const MAX_POLLS = (2 * 60 * 60 * 1000) / POLL_INTERVAL_MS

const BACKFILL_STATUS = {
  SAMPLING: 'sampling',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  ERRORED: 'errored',
}

const TERMINAL_STATUSES = [BACKFILL_STATUS.COMPLETED, BACKFILL_STATUS.ERRORED]

const parseData = (data) => {
  if (isNil(data)) {
    return null
  }

  if (typeof data !== 'string') {
    return data
  }

  try {
    return JSON.parse(data)
  } catch {
    return null
  }
}

export const KodiBackfillProgressMessage = ({ message, onTerminal }) => {
  const dispatch = useDispatch()
  const initial = parseData(message.data)
  const flowUuid = initial?.flow_uuid
  const [status, setStatus] = useState(initial?.backfill_status || BACKFILL_STATUS.IN_PROGRESS)
  const [enqueued, setEnqueued] = useState(initial?.calls_enqueued ?? 0)
  const [processed, setProcessed] = useState(initial?.calls_processed ?? 0)
  // Outside the effect: a status change restarts it, and a counter reset there
  // would hand a job that moves sampling -> in_progress a fresh ceiling.
  const pollCount = useRef(0)
  const onTerminalRef = useRef(onTerminal)
  onTerminalRef.current = onTerminal

  useEffect(() => {
    if (isEmpty(flowUuid) || TERMINAL_STATUSES.includes(status)) {
      return undefined
    }

    let cancelled = false
    let failures = 0
    let timerId = null

    const poll = async () => {
      try {
        const response = await fetchingAPI(
          `${apiService.summary}/bit-flows/${flowUuid}/backfill/status`,
          'GET',
          dispatch,
          undefined, // body
          undefined, // callback
          // This poll retries on its own and gives up after MAX_CONSECUTIVE_FAILURES.
          // Without this, every blip during an hour of 10s polling toasts the user.
          { isSilent: true }
        )

        if (cancelled) {
          return
        }

        failures = 0
        // A status key that has expired reads as completed with a zero count, so
        // keep the last total we knew rather than reporting "0 calls queued" for
        // a run the artifact says queued hundreds.
        setEnqueued((previous) => response.calls_enqueued || previous)
        setProcessed(response.calls_processed ?? 0)
        setStatus(response.backfill_status)

        // Only a BE-confirmed terminal state reaches here, and only once: the
        // terminal status stops the timer and the effect exits early on rerun.
        // A zero total is an expired status key, not a real completion.
        if (TERMINAL_STATUSES.includes(response.backfill_status) && response.calls_enqueued > 0) {
          onTerminalRef.current?.({
            event: `backfill_${response.backfill_status}`,
            flow_uuid: flowUuid,
            calls_enqueued: response.calls_enqueued,
            calls_processed: response.calls_processed ?? 0,
          })
        }

        pollCount.current += 1

        if (
          !TERMINAL_STATUSES.includes(response.backfill_status) &&
          pollCount.current < MAX_POLLS
        ) {
          timerId = setTimeout(poll, POLL_INTERVAL_MS)
        }
      } catch {
        if (cancelled) {
          return
        }

        failures += 1

        if (failures < MAX_CONSECUTIVE_FAILURES) {
          timerId = setTimeout(poll, POLL_INTERVAL_MS)
        } else {
          setStatus(BACKFILL_STATUS.ERRORED)
        }
      }
    }

    timerId = setTimeout(poll, POLL_INTERVAL_MS)

    return () => {
      cancelled = true

      if (!isNil(timerId)) {
        clearTimeout(timerId)
      }
    }
  }, [flowUuid, status, dispatch])

  if (isEmpty(flowUuid)) {
    return null
  }

  const isComplete = status === BACKFILL_STATUS.COMPLETED
  const isErrored = status === BACKFILL_STATUS.ERRORED
  // No denominator yet (the enqueue count lands with the first poll) means the
  // work is real but unmeasurable — Progress renders that honestly rather than
  // showing a made-up 0%.
  // Sampling runs before any call is queued, so there is nothing to count yet.
  const indeterminate = !isComplete && (enqueued === 0 || status === BACKFILL_STATUS.SAMPLING)
  // Retried calls can push the processed counter past the enqueued total, and a
  // label reading "138%" alongside a bar pinned at 100% just looks broken.
  const percent = enqueued === 0 ? 0 : Math.min(100, Math.round((processed / enqueued) * 100))

  let label = `Backfilling ${enqueued} queued calls — ${processed} done (${percent}%)`

  if (indeterminate) {
    label = 'Starting the backfill…'
  }

  // The server reports processed = enqueued once a backfill completes (the live
  // counter is cleared), so a completed run can only honestly claim what it
  // queued. Saying "processed" here would assert a number nobody counted.
  if (isComplete) {
    label = `Backfill finished — ${enqueued} calls queued`
  }

  if (isErrored) {
    label = 'Backfill failed'
  }

  return (
    <div className="cowork-backfill-message">
      <div className="cowork-backfill-message__header">
        {isComplete && (
          <IconCheck size={16} stroke={1.5} className="cowork-backfill-message__icon" />
        )}
        {isErrored && (
          <IconAlertTriangle size={16} stroke={1.5} className="cowork-backfill-message__icon" />
        )}
        <span className="cowork-backfill-message__label">{label}</span>
      </div>
      <Progress
        value={isComplete ? 100 : percent}
        success={isComplete}
        critical={isErrored}
        magical={!isComplete && !isErrored}
        indeterminate={indeterminate && !isErrored}
      />
      {!isComplete && !isErrored && (
        <p className="cowork-backfill-message__hint">
          This runs in the background and usually takes about an hour. You can keep working.
        </p>
      )}
      {isComplete && (
        <p className="cowork-backfill-message__hint">
          This covers every call on this playbook in the selected date range, including calls under
          a minute.
        </p>
      )}
    </div>
  )
}
